import type {
  Account,
  GlucoseEvent as GlucoseUserEvent,
  GlucoseSubEntry,
  Entry as UserEntry,
  Event as UserEvent,
} from "@tandemdiabetes/sugarmate-api-react-client"
import { ActivityType } from "@tandemdiabetes/sugarmate-api-react-client"
import dayjs from "dayjs"
import { useMemo } from "react"
import type {
  DeepReadonlyObject,
  MangoQuery,
} from "rxdb"
import { useFindUserEntries } from "src/hooks/userEntry"
import { useFindUserEvents } from "src/hooks/userEvent"
import type { Reading } from "src/types"
import {
  createEntryQueryObjectForType,
  createEventQueryObjectForType,
  glucoseEventToReading,
  glucoseSubEntryToReading,
} from "src/utils"

interface Params {
  accountId: Account["id"];
  afterTime?: string; // get all readings after this time (inclusive)
  beforeTime?: string; // get all readings before this time (exclusive)
}

/*
 * returns the most recent glucose entry/event query between before/after time.
 */
export function useQueryNewestReading(params: Params) {
  const {
    accountId,
    afterTime,
    beforeTime,
  } = params

  const queryParams = useMemo(
    () => {
      const startedAtSelector: Exclude<MangoQuery<UserEvent & UserEntry>["selector"], undefined>["started_at"] = {}

      if (beforeTime) {
        startedAtSelector["$lt"] = beforeTime
      }

      if (afterTime) {
        startedAtSelector["$gte"] = afterTime
      }

      return {
        selector: { started_at: startedAtSelector },
        sort: [{ started_at: "desc" as const }],
        limit: 1,
      }
    },
    [
      beforeTime,
      afterTime,
    ],
  )

  const previousGlucoseEventQuery = useMemo(
    () => createEventQueryObjectForType(
      accountId,
      ActivityType.Glucose,
      queryParams,
    ),
    [
      queryParams,
      accountId,
    ],
  )

  const previousGlucoseEntryQuery = useMemo(
    () => createEntryQueryObjectForType(
      accountId,
      ActivityType.Glucose,
      queryParams,
    ),
    [
      queryParams,
      accountId,
    ],
  )

  const {
    result: userEvents,
  } = useFindUserEvents<GlucoseUserEvent>(previousGlucoseEventQuery)

  const {
    result: userEntries,
  } = useFindUserEntries(previousGlucoseEntryQuery)

  return useMemo<Reading | null>(
    () => {
      const userEvent = userEvents[0]
      const userEntry = userEntries[0]

      if (userEvent && userEntry) {
        if (dayjs(userEvent.started_at).isAfter(userEntry.started_at)) {
          return glucoseEventToReading(userEvent)
        }
        return entryToReading(userEntry)

      }

      if (userEvent) {
        return glucoseEventToReading(userEvent)
      }

      if (userEntry) {
        return entryToReading(userEntry)
      }

      return null
    },
    [
      userEvents,
      userEntries,
    ],
  )
}

function entryToReading(entry: DeepReadonlyObject<UserEntry>): Reading | null {
  const glucoseSubEntry = entry.subentries.find(
    (subEntry) => subEntry.subentry_type === ActivityType.Glucose,
  ) as GlucoseSubEntry | undefined

  if (!glucoseSubEntry) {
    return null
  }

  return glucoseSubEntryToReading(
    entry.started_at,
    glucoseSubEntry,
  )
}
